/**
 * Flowsheet tugun modellari (4-bosqich: balans darajasidagi apparatlar).
 * Har tugun: kirish oqimlari → chiqish oqimlari + issiqlik yuki Q (J/s, musbat = berilgan).
 *
 * TENGLAMALAR
 *   mixer:     n_out = Σ n_in;  H_out = Σ H_in (adiabatik) → T_out;  P_out = min P_in
 *   splitter:  n_k = φ_k · n_in, T, P o'zgarmas
 *   heater:    Q = H_out(T_out) − H_in   yoki  T_out = T(H_in + Q)
 *   reactor:   ξ_r = X_r · n_key,in / |ν_key|;  n_i,out = n_i,in + Σ_r ν_i,r ξ_r
 *              isothermal: Q = H_out(T) − H_in + Σ ξ_r ΔH_r
 *              adiabatic:  H_out(T_out) = H_in − Σ ξ_r ΔH_r  → T_out
 *   separator: n_i,top = σ_i n_i,in; n_i,bottom = (1−σ_i) n_i,in; Q = ΔH (T berilsa)
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR: stream.ts ga qarang; reaktorda ΔH_r doimiy;
 * konversiya belgilangan (kinetika 5-bosqichda `reactions` kutubxonasi orqali keladi).
 * Manfiy sarf chiqsa 0 ga tortiladi va `flowsheet.negativeFlow` ogohlantirish beriladi.
 */
import type { Phase, WarningSink } from '../types';
import { warn } from '../types';
import type { FlowsheetNode } from './schema';
import { cloneStream, emptyStream, enthalpy, temperatureFromEnthalpy, type Stream } from './stream';
import { computeKineticReactor, type BedReport } from './reactors';
import { bubbleT } from '../thermo';
import { getSubstance } from '../properties';

export interface NodeContext {
  components: readonly string[];
  phase: Phase;
  Tref: number;
  warnings: WarningSink;
}

export interface NodeResult {
  /** chiqish oqimi id → oqim */
  outlets: Record<string, Stream>;
  /** issiqlik yuki, J/s (musbat — tashqaridan berilgan) */
  Q: number;
  /** reaktor: reaksiya issiqligi yig'indisi −Σ ξ ΔH_r (J/s), konversiyalar */
  extent?: Record<string, number>;
  reactionHeat?: number;
  T: number;
  /** kinetik reaktorlar */
  conversion?: Record<string, number>;
  selectivity?: Record<string, number>;
  equilibriumConversion?: Record<string, number>;
  beds?: BedReport[];
  profile?: { V: number; T: number; X: number }[];
  steadyStates?: { T: number; conversion: number }[];
  converged?: boolean;
}

/** Tugunni hisoblash. `inlets` — kirish oqimlari (sxema tartibida), `outletIds` — chiqish oqim id lari */
export function computeNode(node: FlowsheetNode, inlets: Stream[], outletIds: string[], ctx: NodeContext): NodeResult {
  const n = node;
  const num = (v: number | string, what: string): number => {
    if (typeof v !== 'number') throw new Error(`${node.id}: ${what} unresolved variant reference ${v}`);
    return v;
  };
  const sum = (): Stream => {
    const out = emptyStream(outletIds[0] ?? `${node.id}.out`, ctx.components);
    let H = 0;
    let P = Infinity;
    for (const s of inlets) {
      for (const [c, f] of Object.entries(s.flow)) out.flow[c] = (out.flow[c] ?? 0) + f;
      H += enthalpy(s, ctx.phase, ctx.Tref, ctx.warnings);
      P = Math.min(P, s.P);
    }
    out.P = Number.isFinite(P) ? P : 101325;
    out.T = inlets.length === 1 ? (inlets[0] as Stream).T : temperatureFromEnthalpy(out, H, ctx.phase, ctx.Tref);
    return out;
  };

  switch (n.type) {
    case 'feed': {
      const out = emptyStream(outletIds[0] ?? `${node.id}.out`, ctx.components, num(n.T, 'T'), num(n.P, 'P'));
      for (const [c, f] of Object.entries(n.flow)) {
        if (!ctx.components.includes(c)) throw new Error(`${node.id}: unknown component ${c}`);
        out.flow[c] = num(f, `flow.${c}`);
      }
      return { outlets: { [out.id]: out }, Q: 0, T: out.T };
    }
    case 'product':
      return { outlets: {}, Q: 0, T: inlets[0]?.T ?? ctx.Tref };
    case 'mixer': {
      const out = sum();
      return { outlets: { [out.id]: out }, Q: 0, T: out.T };
    }
    case 'splitter': {
      const inn = sum();
      const outlets: Record<string, Stream> = {};
      // Bitta chiqish uchun ulush berilmasa — qoldiq (1 − Σ boshqalar)
      const missing = outletIds.filter((id) => n.fractions[id] === undefined);
      if (missing.length > 1) throw new Error(`${node.id}: splitter fractions missing for ${missing.join(', ')}`);
      const given = outletIds.filter((id) => n.fractions[id] !== undefined).reduce((a, id) => a + num(n.fractions[id] as number | string, `fractions.${id}`), 0);
      let total = 0;
      for (const id of outletIds) {
        const phi = n.fractions[id] === undefined ? 1 - given : num(n.fractions[id] as number | string, `fractions.${id}`);
        if (phi < -1e-12) throw new Error(`${node.id}: splitter fractions exceed 1`);
        total += phi;
        const s = cloneStream(inn);
        s.id = id;
        for (const c of Object.keys(s.flow)) s.flow[c] = (s.flow[c] as number) * phi;
        outlets[id] = s;
      }
      if (Math.abs(total - 1) > 1e-9) throw new Error(`${node.id}: splitter fractions must sum to 1 (got ${total})`);
      return { outlets, Q: 0, T: inn.T };
    }
    case 'heater': {
      const inn = sum();
      const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
      const out = cloneStream(inn);
      let Q: number;
      if (n.outletT !== undefined) {
        out.T = num(n.outletT, 'outletT');
        Q = enthalpy(out, ctx.phase, ctx.Tref, ctx.warnings) - Hin;
      } else {
        Q = num(n.duty ?? 0, 'duty');
        out.T = temperatureFromEnthalpy(out, Hin + Q, ctx.phase, ctx.Tref);
      }
      return { outlets: { [out.id]: out }, Q, T: out.T };
    }
    case 'reactor': {
      const inn = sum();
      const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
      const out = cloneStream(inn);
      const extent: Record<string, number> = {};
      let heat = 0; // −Σ ξ ΔH_r (musbat — ekzotermik ajratadi)
      for (const r of n.reactions) {
        const nuKey = r.stoich[r.key];
        if (nuKey === undefined || nuKey >= 0) throw new Error(`${node.id}: reaction ${r.id}: key ${r.key} must be a reactant (negative stoich)`);
        const X = num(r.conversion, `reactions.${r.id}.conversion`);
        if (X < 0 || X > 1) throw new Error(`${node.id}: conversion out of [0,1]`);
        const xi = (X * (out.flow[r.key] ?? 0)) / -nuKey;
        extent[r.id] = xi;
        for (const [c, nu] of Object.entries(r.stoich)) {
          if (!ctx.components.includes(c)) throw new Error(`${node.id}: unknown component ${c}`);
          const v = (out.flow[c] ?? 0) + nu * xi;
          if (v < -1e-12) {
            warn(ctx.warnings, 'flowsheet.negativeFlow', 'warning', { node: node.id, component: c });
            out.flow[c] = 0;
          } else out.flow[c] = Math.max(0, v);
        }
        heat += -xi * num(r.dHr, `reactions.${r.id}.dHr`);
      }
      let Q = 0;
      if (n.mode === 'adiabatic') {
        out.T = temperatureFromEnthalpy(out, Hin + heat, ctx.phase, ctx.Tref);
      } else {
        out.T = n.T !== undefined ? num(n.T, 'T') : inn.T;
        Q = enthalpy(out, ctx.phase, ctx.Tref, ctx.warnings) - Hin - heat;
      }
      return { outlets: { [out.id]: out }, Q, extent, reactionHeat: heat, T: out.T };
    }
    case 'cstr':
    case 'pfr':
    case 'bed-reactor': {
      const inn = sum();
      const r = computeKineticReactor(n, inn, outletIds[0] ?? `${node.id}.out`, ctx);
      return {
        outlets: { [r.outlet.id]: r.outlet },
        Q: r.Q,
        extent: r.extent,
        reactionHeat: r.reactionHeat,
        T: r.outlet.T,
        conversion: r.conversion,
        selectivity: r.selectivity,
        ...(r.equilibriumConversion ? { equilibriumConversion: r.equilibriumConversion } : {}),
        ...(r.beds ? { beds: r.beds } : {}),
        ...(r.profile ? { profile: r.profile } : {}),
        ...(r.steadyStates ? { steadyStates: r.steadyStates } : {}),
        converged: r.converged,
      };
    }
    case 'column': {
      const inn = sum();
      const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
      const P = n.P !== undefined ? num(n.P, 'P') : inn.P;
      const remaining: Record<string, number> = { ...inn.flow };
      const outlets: Record<string, Stream> = {};
      let Hout = 0;
      const cutT = (flow: Record<string, number>, given: number | string | undefined, what: string): number => {
        if (given !== undefined) return num(given, what);
        // Raoult qaynash harorati: faqat Antoine'li (uchuvchan) komponentlar; uchmaydiganlar K = 0
        const x: Record<string, number> = {};
        let tot = 0;
        for (const [c, f] of Object.entries(flow)) if (f > 0) tot += f;
        if (tot <= 0) return inn.T;
        let volatile = 0;
        for (const [c, f] of Object.entries(flow)) {
          if (f <= 0) continue;
          if (getSubstance(c).antoine) {
            x[c] = f / tot;
            volatile += f / tot;
          }
        }
        if (volatile <= 0) return inn.T;
        const r = bubbleT(x, P, [200, 900]);
        if (!r.converged) warn(ctx.warnings, 'flowsheet.bubbleTFailed', 'warning', { node: node.id });
        return r.converged ? r.value : inn.T;
      };
      for (const cut of n.cuts) {
        const s = emptyStream(cut.outlet, ctx.components, inn.T, P);
        for (const c of ctx.components) {
          const sigma = cut.split[c] === undefined ? 0 : num(cut.split[c] as number | string, `cuts.${cut.outlet}.split.${c}`);
          const take = Math.min(remaining[c] ?? 0, sigma * (inn.flow[c] ?? 0));
          s.flow[c] = take;
          remaining[c] = (remaining[c] ?? 0) - take;
        }
        s.T = cutT(s.flow, cut.T, `cuts.${cut.outlet}.T`);
        outlets[s.id] = s;
        Hout += enthalpy(s, ctx.phase, ctx.Tref, ctx.warnings);
      }
      const bottom = emptyStream(n.bottom, ctx.components, inn.T, P);
      for (const c of ctx.components) bottom.flow[c] = Math.max(0, remaining[c] ?? 0);
      bottom.T = n.bottomT !== undefined ? num(n.bottomT, 'bottomT') : cutT(bottom.flow, undefined, 'bottomT');
      outlets[bottom.id] = bottom;
      Hout += enthalpy(bottom, ctx.phase, ctx.Tref, ctx.warnings);
      return { outlets, Q: Hout - Hin, T: bottom.T };
    }
    case 'separator': {
      const inn = sum();
      const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
      const T = n.T !== undefined ? num(n.T, 'T') : inn.T;
      const top = emptyStream(n.outlets.top, ctx.components, T, inn.P);
      const bottom = emptyStream(n.outlets.bottom, ctx.components, T, inn.P);
      for (const c of ctx.components) {
        const sigma = n.split[c] === undefined ? 0 : num(n.split[c] as number | string, `split.${c}`);
        if (sigma < 0 || sigma > 1) throw new Error(`${node.id}: split.${c} out of [0,1]`);
        const f = inn.flow[c] ?? 0;
        top.flow[c] = sigma * f;
        bottom.flow[c] = (1 - sigma) * f;
      }
      const Q = enthalpy(top, ctx.phase, ctx.Tref, ctx.warnings) + enthalpy(bottom, ctx.phase, ctx.Tref, ctx.warnings) - Hin;
      return { outlets: { [top.id]: top, [bottom.id]: bottom }, Q, T };
    }
  }
  throw new Error(`Unknown node type: ${String((node as { type: unknown }).type)}`);
}
